import { useEffect, useState } from 'react';

/**
 * Window Controls Overlay support detection.
 *
 * Quand la PWA est installée et que le navigateur supporte WCO, la barre de titre
 * native est masquée et l'app peut peindre dans la zone titre. On expose le statut
 * pour que les composants ajustent leur drag region et leur padding.
 *
 * Side-effect : ajoute `wco-active` sur <html> pour les styles globaux dans index.css.
 */
interface WindowControlsOverlayLike {
  readonly visible: boolean;
  addEventListener(type: 'geometrychange', listener: () => void): void;
  removeEventListener(type: 'geometrychange', listener: () => void): void;
}

type NavigatorWithWCO = Navigator & {
  windowControlsOverlay?: WindowControlsOverlayLike;
};

export function useWindowControlsOverlay(): boolean {
  const [active, setActive] = useState<boolean>(() => readInitialActive());

  useEffect(() => {
    const nav = navigator as NavigatorWithWCO;
    const wco = nav.windowControlsOverlay;
    if (!wco) return;

    const update = (): void => {
      setActive(wco.visible);
      document.documentElement.classList.toggle('wco-active', wco.visible);
    };

    update();
    wco.addEventListener('geometrychange', update);
    return () => {
      wco.removeEventListener('geometrychange', update);
    };
  }, []);

  return active;
}

function readInitialActive(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as NavigatorWithWCO;
  return Boolean(nav.windowControlsOverlay?.visible);
}
